import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  auth,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  ensureUserProfileDocument
} from '@/firebase'; 
import { Mail, Lock, Home, AlertCircle, CheckCircle2, ShieldCheck, Globe, Database, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';

/**
 * 🔐 Login Component v3.0
 * Refactored for Hybrid Scoping. Ensures every login initializes the 
 * Personal Partition (/users/{uid}) before checking Household Hub status.
 */
export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [authMode, setAuthMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * 🛡️ Authentication Protocol
   * Adheres to Google API Limited Use Policy.
   */
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (authMode === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // 🛰️ Initializing Personal Partition immediately
        await ensureUserProfileDocument(userCredential.user); 
        setMessage(t('login.signup_success', 'Account initialized. Personal partition ready.'));
        setAuthMode('signin');
      } else if (authMode === 'signin') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await ensureUserProfileDocument(userCredential.user);
        // 🚀 Redirect to the Hybrid Dashboard
        navigate('/dashboard');
      } else if (authMode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setMessage(t('login.reset_email_sent', 'Security reset protocol initiated. Check your inbox.'));
        setAuthMode('signin');
      }
    } catch (err: any) {
      let errorMessage = t('login.err_generic', 'Internal authentication failure.');
      if (err.code === 'auth/email-already-in-use') errorMessage = t('login.err_email_in_use', 'Email registered.');
      else if (err.code === 'auth/weak-password') errorMessage = t('login.err_weak_password', 'Insecure password.');
      else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') errorMessage = t('login.err_invalid_credentials', 'Incorrect credentials.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      // 🛡️ Ensure Personal Space (/users/{uid}) is established
      await ensureUserProfileDocument(userCredential.user); 
      navigate('/dashboard');
    } catch (err: any) {
      setError(t('login.err_google', 'Google handshake failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950 p-6 relative overflow-x-hidden">
      
      {/* 🎨 Decoration (Unchanged) */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* 🏛️ Brand Logo (Unchanged) */}
      <div onClick={() => navigate('/')} className="flex flex-col items-center gap-3 mb-10 cursor-pointer group z-10">
        <div className="p-3 rounded-2xl bg-indigo-600 text-white shadow-xl group-hover:scale-110 transition-transform">
          <Home className="w-8 h-8" />
        </div>
        <div className="flex items-center gap-1 font-black text-3xl tracking-tighter text-slate-950 dark:text-gray-200">
          Everyday<span className="text-indigo-600 dark:text-indigo-400">OS</span>
        </div>
      </div>

      <div className="relative w-full max-w-md z-10">
        <div className="w-full p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl">
          
          {/* 🛡️ HYBRID SCOPING ANNOUNCEMENT */}
          <div className="mb-8 flex items-center gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800/30">
             <div className="flex -space-x-2">
               <div className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                 <User className="w-3 h-3 text-emerald-500" />
               </div>
               <div className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                 <Users className="w-3 h-3 text-indigo-500" />
               </div>
             </div>
             <p className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-tighter">
                {t('login.hybrid_enabled', 'Hybrid Scoping Enabled')}
             </p>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-slate-950 dark:text-gray-200 mb-2 tracking-tight uppercase italic">
              {authMode === 'signin' ? 'Enter OS' : 'Initialize'}
            </h2>
            <p className="text-slate-500 dark:text-gray-400 text-xs font-medium px-4">
              {authMode === 'signin' && t('login.signin_subtitle', 'Authorized Access Only.')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300 font-bold">{error}</p>
            </div>
          )}

          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-600 rounded-2xl text-slate-950 dark:text-gray-200 font-bold outline-none transition-all"
                placeholder="example@email.com"
              />
            </div>

            {authMode !== 'reset' && (
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-600 rounded-2xl text-slate-950 dark:text-gray-200 font-bold outline-none transition-all"
                  placeholder="password"
                />
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-2xl h-14 font-black text-xs uppercase bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl shadow-indigo-500/20 tracking-widest"
              isLoading={loading}
            >
              {authMode === 'reset' ? 'DISPATCH RESET' : authMode === 'signup' ? 'INITIALIZE PERSONAL SPACE' : 'LOGIN'}
            </Button>
          </form>

          <div className="relative flex items-center justify-center my-8">
            <div className="border-t border-slate-100 dark:border-slate-800 w-full"></div>
            <span className="bg-white dark:bg-slate-900 px-4 text-[9px] font-black text-slate-400 uppercase tracking-widest absolute">OAuth 2.0 Bridge</span>
          </div>

          <Button
            variant="outline"
            size="lg"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full rounded-2xl h-14 border-2 border-slate-100 dark:border-slate-800 font-bold"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 mr-3" alt="Google" />
            {t('login.google_btn', 'Google Login')}
          </Button>

          {/* ⚖️ GOOGLE COMPLIANCE DISCLOSURE */}
          <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
            <p className="text-[10px] leading-relaxed text-slate-500 dark:text-slate-400 font-medium">
              By continuing, EverydayOS will access your Google profile (name, email, and avatar) to initialize your personal partition. We use this data strictly for account identification and to personalize your dashboard. We do not sell your data or use it for advertising. You can review Google's  
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" className="text-indigo-600 dark:text-indigo-400 underline mx-1">Limited Use Policy</a>.
            </p>
          </div>
        </div>

        {/* 🛡️ DATA SAFETY SIDECAR (Modified for Hybrid Info) */}
        <div className="lg:absolute lg:left-full lg:ml-8 lg:top-0 w-full lg:w-64 mt-8 lg:mt-0 space-y-4">
<div className="p-6 bg-indigo-600 rounded-[2rem] text-white shadow-xl flex flex-col gap-4">
<ShieldCheck className="w-6 h-6 opacity-80" />
<div>
<h4 className="font-black text-xs uppercase tracking-widest mb-1">Hybrid Partitioning</h4>
<p className="text-[10px] font-bold opacity-90 leading-relaxed">
Personal data targets <code className="bg-white/10 px-1 rounded">users</code>.
Shared data targets <code className="bg-white/10 px-1 rounded">households</code>.
</p>
</div>
<div className="pt-2 border-t border-white/20">
<h4 className="font-black text-[9px] uppercase tracking-[0.2em] mb-2 text-emerald-300">Privacy Protocol 2026</h4>
<p className="text-[9px] font-bold opacity-80 leading-tight">
Zero-sharing policy: Personal data is never exposed to third-party AI training or external surveillance. 
</p>
</div>
<ul className="space-y-2 pt-2 border-t border-white/20">
<li className="flex gap-2 items-center">
<div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
<span className="text-[9px] font-black uppercase tracking-wider">Verified Data Isolation</span>
</li>
<li className="flex gap-2 items-center">
<div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
<span className="text-[9px] font-black uppercase tracking-wider">Minimal Profile Access</span>
</li>
</ul>
<div className="bg-white/10 p-3 rounded-xl">
<p className="text-[8px] font-black uppercase tracking-tighter leading-none opacity-70">
System Status:
</p>
{/*<p className="text-[10px] font-bold text-emerald-300 mt-1 uppercase italic">
Privacy Compliant • Verified
</p>*/}
</div>
</div>
</div>
      </div>
    </div>
  );
};